import { useCallback, useEffect, useRef } from 'react';
import { Button } from '@accure/ui';
import { format } from 'date-fns';
import { ChevronRight, Edit3, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';

import { Card } from '@/components/ui/card';
import { codec, useQueryParams } from '@/hooks/router/use-query-params';
import type { Task } from '@/project-management/entities/task';
import { type Workstream } from '@/project-management/entities/workstream';
import { useAddTaskModal } from '@/project-management/modals/add-task';
import { useDeleteWorkstreamModal } from '@/project-management/modals/delete-workstream';
import { useEditWorkstreamModal } from '@/project-management/modals/edit-workstream';
import { WorkstreamQuery } from '@/project-management/queries/workstream.query';
import { cls } from '@/utils/cls';

import { WorkstreamEmptyTasks } from './workstream-empty-tasks';
import { WorkstreamProgress } from './workstream-progress';
import { WorkstreamTask } from './workstream-task';

export type WorkstreamProps = {
  workstream: Pick<
    Workstream,
    | 'id'
    | 'name'
    | 'description'
    | 'progress'
    | 'startDate'
    | 'endDate'
    | 'responsible'
  > & {
    tasks: Pick<Task, 'id' | 'status' | 'title' | 'description' | 'assignee'>[];
  };
};

export function Workstream({ workstream }: WorkstreamProps) {
  const showDeleteWorkstreamModal = useDeleteWorkstreamModal();
  const showEditWorkstreamModal = useEditWorkstreamModal();
  const showAddTaskModal = useAddTaskModal();
  const prefetchWorkstream = WorkstreamQuery.usePrefetch();
  const cardRef = useRef<HTMLDivElement>(null);

  const [{ expandedWorkstreamId }, setExpandedWorkstreamId] = useQueryParams({
    expandedWorkstreamId: {
      key: 'id',
      schema: z.string().optional(),
      codec: codec.string,
    },
  });

  const isExpanded = expandedWorkstreamId === workstream.id;

  useEffect(() => {
    if (isExpanded && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [isExpanded]);

  const toggleExpanded = useCallback(
    function () {
      setExpandedWorkstreamId.replace({
        expandedWorkstreamId: isExpanded ? undefined : workstream.id,
      });
    },
    [isExpanded, setExpandedWorkstreamId, workstream.id],
  );

  return (
    <div ref={cardRef}>
      <Card
        className="space-y-6 p-6"
        onMouseEnter={() => prefetchWorkstream({ id: workstream.id })}
      >
        <div className="grid grid-cols-[1fr_min-content] gap-2">
          <div className="grid grid-rows-[min-content_1fr_min-content] gap-3">
            <div className="grid grid-cols-[min-content_1fr] gap-2">
              <button onClick={toggleExpanded} className="-m-2 p-2">
                <ChevronRight
                  className={cls('size-4 text-gray-400 transition-transform', {
                    'rotate-90': isExpanded,
                  })}
                />
              </button>
              <div className="truncate text-lg font-semibold">
                {workstream.name}
              </div>
            </div>
            <div className="text-gray-600">{workstream.description ?? ''}</div>
            <WorkstreamProgress
              progress={workstream.progress}
              className="max-w-lg"
            />
          </div>
          <div className="grid grid-rows-[1fr_min-content_min-content]">
            <div className="flex flex-row items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                onClick={() =>
                  showAddTaskModal({
                    formType: 'create',
                    workstreamId: workstream.id,
                  })
                }
              >
                <Plus className="size-3.5" />
                Add task
              </Button>

              <div className="h-4 w-px bg-gray-300"></div>

              <Button
                variant="ghost"
                size="sm"
                className="p-1 text-gray-400 hover:text-gray-600"
                onClick={() => showEditWorkstreamModal({ id: workstream.id })}
              >
                <Edit3 className="size-4" />
              </Button>

              <div className="h-4 w-px bg-gray-300"></div>

              <Button
                variant="ghost"
                size="sm"
                className="p-1 text-gray-400 hover:text-red-500"
                onClick={() => showDeleteWorkstreamModal({ id: workstream.id })}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
            <p className="whitespace-nowrap text-right text-sm text-gray-600">
              Responsible: {workstream.responsible ?? '-'}
            </p>
            <p className="whitespace-nowrap text-right text-sm text-gray-600">
              {format(workstream.startDate, 'MMM d')} -{' '}
              {format(workstream.endDate, 'MMM d')}
            </p>
          </div>
        </div>
        <div
          className={cls('space-y-3 border-t pt-6', { hidden: !isExpanded })}
        >
          {workstream.tasks.length === 0 ? (
            <WorkstreamEmptyTasks workstream={workstream} />
          ) : (
            workstream.tasks.map((task) => (
              <WorkstreamTask
                key={task.id}
                task={task}
                workstream={workstream}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
}
